import type {
  ClientLogSubmissionContent,
  ClientLogSubmissionRecord,
  UserStoreState,
} from '../types/stores'
import type { ContactObject } from '../types/intercom'

const defaultData: UserStoreState = {
  money: 0,
  id: -1,
  email: '',
  reg_ip: '',
  token: '',
  no_email: false,
  web_login: true,
  extra: {},
  billing: {
    firstname: '',
    lastname: '',
    addr: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    company_vat: '',
    company_name: '',
  },
  created_at: '',
  updated_at: '',
  balance_currency: 'eur',
  org_id: null,
  crisp_hash: '',
  site_domain: '',
  riskRecord: null as highRiskUsersAttributes | null,
  jwt_token: '',
}

export const useUser = defineStore('user', {
  state: (): UserStoreState => {
    return { ...defaultData }
  },
  getters: {
    user_id(): number {
      return this.id as number
    },
    loggedIn(): boolean {
      return this.id !== -1
    },
    tk_suspend(): boolean {
      return this.extra.tk_suspend || false
    },
    allowedPaymentMethodsFromServer(): string[] {
      return useUserCache().allowedPaymentMethodsCache.get(String(this.id)) || []
    },
  },
  actions: {
    async get({ id, reload = false }: { id: number | string; reload?: boolean }): Promise<UserData | null> {
      // console.log('4-----------------------------------------')
      const data = await this.getUserInfo({ id, reload })
      if (data && this.id !== data?.id) {
        this.setUserData(data)
        useUserCache().userDataCache.set(String(id), data)
      }
      return data
    },
    async getUserInfo({ id, reload = false }: { id: number | string; reload?: boolean }): Promise<UserData | null> {
      if (!reload) {
        if (this.id === Number(id)) {
          // console.log('return store data', 'this.id', this.id, 'Number(id)', Number(id))
          return this
        }
        if (useUserCache().userDataCache.has(String(id))) {
          // console.log('found user data in cache')
          // if (this.id !== UserDataCache.get(id)?.id) {
          //   this.setUserData(UserDataCache.get(id)!)
          // }
          return useUserCache().userDataCache.get(String(id)) || null
        }
      }
      // console.log('initializing useAPI')
      // const api = useAPI()
      const data = (await useAPI().get('/users/' + id, { key: `user-${id}` })) as UserData
      if (data) {
        useUserCache().userDataCache.set(String(id), data)
        if (data.id !== id) {
          useUserCache().userDataCache.set(String(data.id!), data)
        }
      }
      // } else {
      //   this.setUserData(data)
      // }
      return data
    },
    setUserData(data: UserData | null): void {
      if (data) {
        if (data.id !== this.id) {
          // user changed, reset all to default first, otherwise some keys will stale and cause issues.
          this.$state = { ...defaultData }
        }
        // console.log('setUserData', data)
        for (const key in data) {
          if (key === 'user_id') {
            continue
          }
          this[key] = data[key]
          // console.log(key, '->', this[key])
        }
      }
    },
    async signUp(postData: { email: string; password: string; ref: string | number | null }): Promise<UserData> {
      return (await useAPI().post('/users', { body: postData })) as UserData
    },
    async requestPasswordReset(postData: { email: string }): Promise<{ email_token: string }> {
      const data = (await useAPI().post('users/reset_password', { body: postData })) as { email_token: string }
      return data
    },
    async updateBilling({ billing }: { billing: BillingAddress | null }): Promise<any> {
      const data = await useAPI().post(`users/${this.id}/billing`, { body: { billing } })
      this.billing = billing || {
        firstname: '',
        lastname: '',
        addr: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        company_name: '',
        company_vat: '',
      }
      return data
    },
    async getLastPaymentBilling(): Promise<{
      billing: BillingAddress | null
      payment_id?: number
      payment_date?: string
      payment_channel?: string
      message?: string
    }> {
      const data = (await useAPI().get(`users/${this.id}/last-payment-billing`)) as {
        billing: BillingAddress | null
        payment_id?: number
        payment_date?: string
        payment_channel?: string
        message?: string
      }
      return data
    },
    async requstEmailVerification(): Promise<{ email_token2: string }> {
      const data = (await useAPI().post(`users/${this.id}/email_verify`, {})) as { email_token2: string }
      return data
    },
    async setEmailVerified(): Promise<boolean> {
      await useAPI().put(`users/${this.id}/set_email_verified`, {})
      this.extra.email_verified = true
      return true
    },
    async announcementEmail({ sub }: { sub: boolean }): Promise<boolean> {
      await useAPI().put(`users/${this.id}/no_email`, { body: { no_email: !sub } })
      this.no_email = !sub
      return !sub
    },
    async changeEmail({ new_email, password }: { new_email: string; password: string }): Promise<string> {
      await useAPI().put(`users/${this.id}/email`, {
        body: {
          new_email,
          password,
        },
      })
      this.email = new_email
      return new_email
    },
    changePassword({
      old_password,
      new_password,
    }: {
      old_password: string
      new_password: string
    }): Promise<{ jwt_token: string }> {
      return useAPI().put(`users/${this.id}/password`, {
        body: {
          old_password,
          new_password,
        },
      }) as Promise<{ jwt_token: string }>
    },
    getEmailRecords({ filterSubject = [''] }: { filterSubject: string[] }): Promise<EmailRecords[]> {
      return useAPI().get(`users/${this.id}/email_records`, { params: { filterSubject } }) as Promise<EmailRecords[]>
    },
    getLoginRecords(): Promise<LoginRecords[]> {
      return useAPI().get(`users/${this.id}/login_records`, { params: {} }) as Promise<LoginRecords[]>
    },
    getClientLogs(): Promise<ClientLogSubmissionRecord[]> {
      return useAPI().get(`users/${this.id}/client_log_submit`, { params: {} }) as Promise<ClientLogSubmissionRecord[]>
    },
    getClientLogContent({ logId }: { logId: string }): Promise<ClientLogSubmissionContent> {
      return useAPI().get(`users/${this.id}/client_log_submit/${logId}`, { params: {} }) as Promise<ClientLogSubmissionContent>
    },
    getNewPassword({
      new_password,
      token,
      check,
    }: {
      new_password?: string
      token: string
      check?: boolean
    }): Promise<any> {
      return useAPI().post('users/reset_password/confirm', {
        body: {
          new_password,
          token,
          check,
        },
      })
    },
    requestAccountDeletion(): Promise<{ email: string }> {
      return useAPI().post(`users/${this.id}/account_deletion`, {}) as Promise<{ email: string }>
    },
    confirmAccountDeletion({ token, check }: { token: string; check?: boolean }): Promise<{ email?: string }> {
      return useAPI().post('users/account_deletion/confirm', {
        body: {
          token,
          check,
        },
      }) as Promise<{ email?: string }>
    },
    updateExtra(extraData: UserExtra): void {
      this.extra = { ...this.extra, ...extraData }
    },
    barkNotify(opt: {
      title: string
      body: string
      interruptionLevel: 'time-sensitive'
      options: {
        forceAll: boolean
      }
    }): Promise<any> {
      return useAPI().post(`apn-bark/${this.id}/notify`, {
        body: opt,
      }) as Promise<any>
    },
    // getBarkDevices() {
    //   return useAPI().get(`apn-bark/${this.id}/devices`)
    // },
    updateBarkDevice({
      deviceToken,
      data,
    }: {
      deviceToken: string
      data: barkdevicesAttributes
    }): Promise<barkdevicesAttributes> {
      return useAPI().put(`apn-bark/${this.id}/${deviceToken}`, { body: data }) as Promise<barkdevicesAttributes>
    },
    delBarkDevice({ deviceToken }: { deviceToken: string }): Promise<any> {
      return useAPI().delete(`apn-bark/${this.id}/${deviceToken}`, {})
    },
    getAllowedPaymentMethods(userId: number | string = this.id): Promise<string[]> {
      const key = String(userId)
      const cache = useUserCache()
      if (cache.allowedPaymentMethodsCache.has(key)) {
        return Promise.resolve(cache.allowedPaymentMethodsCache.get(key)!)
      }
      const pendingRequest = cache.allowedPaymentMethodsRequests.get(key)
      if (pendingRequest) {
        return pendingRequest
      }

      const apiRequest = useAPI().get(`users/${userId}/allowed_payment_methods`) as Promise<string[]>
      const request: Promise<string[]> = apiRequest
        .then((data) => {
          if (!Array.isArray(data) || data.some(method => typeof method !== 'string')) {
            throw new Error('Invalid allowed payment methods response')
          }
          const methods = data
          if (cache.allowedPaymentMethodsRequests.get(key) === request) {
            cache.allowedPaymentMethodsCache.set(key, methods)
          }
          return methods
        })
        .finally(() => {
          if (cache.allowedPaymentMethodsRequests.get(key) === request) {
            cache.allowedPaymentMethodsRequests.delete(key)
          }
        })
      cache.allowedPaymentMethodsRequests.set(key, request)
      return request
    },
    invalidateAllowedPaymentMethods(userId: number | string = this.id): void {
      const key = String(userId)
      const cache = useUserCache()
      cache.allowedPaymentMethodsCache.delete(key)
      cache.allowedPaymentMethodsRequests.delete(key)
    },
    refreshAllowedPaymentMethods(userId: number | string = this.id): Promise<string[]> {
      this.invalidateAllowedPaymentMethods(userId)
      return this.getAllowedPaymentMethods(userId)
    },
    getIntercomProfile(): Promise<ContactObject> {
      return useAPI().get(`users/${this.id}/intercom_profile`) as Promise<ContactObject>
    },
    syncStripeEmail(opt: { email?: string }): Promise<any> {
      return useAPI().post(`users/${this.id}/sync_stripe_email`, { body: opt })
    },
    setPreferences(prefs: UserPreferencesChangables): Promise<UserPreferencesChangables | null> {
      if (this.id === -1) {
        return Promise.resolve(null)
      }
      return useAPI().put(`users/${this.id}/preferences`, { body: prefs }) as Promise<UserPreferencesChangables>
    },
    getBarkDevices(): Promise<barkdevicesAttributes[]> {
      return useAPI().get(`apn-bark/${this.id}/devices`) as Promise<barkdevicesAttributes[]>
    },
    getBaoLianDengPushDevices(): Promise<AppPushDeviceAttributes[]> {
      return useAPI().get(`app-push-devices/${this.id}/devices?project=baoliandeng&platform=ios`) as Promise<AppPushDeviceAttributes[]>
    },
    updateBaoLianDengPushDevice({
      deviceUuid,
      data,
    }: {
      deviceUuid: string
      data: Partial<AppPushDeviceAttributes>
    }): Promise<AppPushDeviceAttributes> {
      return useAPI().put(`app-push-devices/${this.id}/${deviceUuid}?project=baoliandeng&platform=ios`, { body: data }) as Promise<AppPushDeviceAttributes>
    },
    delBaoLianDengPushDevice({ deviceUuid }: { deviceUuid: string }): Promise<any> {
      return useAPI().delete(`app-push-devices/${this.id}/${deviceUuid}?project=baoliandeng&platform=ios`, {})
    },
    baoLianDengPushNotify(opt: AppPushNotifyRequest): Promise<AppPushNotifyResponse> {
      return useAPI().post(`app-push-devices/${this.id}/notify`, {
        body: { project: 'baoliandeng', platform: 'ios', ...opt },
      }) as Promise<AppPushNotifyResponse>
    },
    createIntercomConversation(opt: { content: string }): Promise<{
      type: string
      id: string
      created_at: number
      subject?: string
      body: string
      message_type: 'email' | 'inapp' | 'facebook' | 'twitter'
      conversation_id: string
    }> {
      return useAPI().post(`users/${this.id}/intercom_conversation`, { body: opt }) as Promise<{
        type: string
        id: string
        created_at: number
        subject?: string
        body: string
        message_type: 'email' | 'inapp' | 'facebook' | 'twitter'
        conversation_id: string
      }>
    },
  },
})
